import { ObjectValues } from 'types';

export const NDA_PROVIDERS = {
  DOCUSIGN: 'docusign',
  NATIVE: 'native',
} as const;

export type NdaProvider = ObjectValues<typeof NDA_PROVIDERS>;

export const AGREEMENT_FIELD_TYPES = {
  SIGNATURE: 'signature',
  FULL_NAME: 'full_name',
  EMAIL: 'email',
  DATE_SIGNED: 'date_signed',
  TIME_SIGNED: 'time_signed',
  PHONE: 'phone',
  COMPANY: 'company',
  ABN: 'abn',
  FUNDING_SOURCE: 'funding_source',
  PURCHASE_BUDGET: 'purchase_budget',
  CUSTOM_TEXT: 'custom_text',
  CHECKBOX: 'checkbox',
} as const;

export type AgreementFieldType = ObjectValues<typeof AGREEMENT_FIELD_TYPES>;

export const AGREEMENT_FIELD_GROUPS = {
  SIGNATURE: 'signature',
  AUTO: 'auto',
  BUYER: 'buyer',
} as const;

export type AgreementFieldGroup = ObjectValues<typeof AGREEMENT_FIELD_GROUPS>;

export const AGREEMENT_FIELD_GROUP_BY_TYPE: Record<
  AgreementFieldType,
  AgreementFieldGroup
> = {
  [AGREEMENT_FIELD_TYPES.SIGNATURE]: AGREEMENT_FIELD_GROUPS.SIGNATURE,
  [AGREEMENT_FIELD_TYPES.FULL_NAME]: AGREEMENT_FIELD_GROUPS.AUTO,
  [AGREEMENT_FIELD_TYPES.EMAIL]: AGREEMENT_FIELD_GROUPS.AUTO,
  [AGREEMENT_FIELD_TYPES.DATE_SIGNED]: AGREEMENT_FIELD_GROUPS.AUTO,
  [AGREEMENT_FIELD_TYPES.TIME_SIGNED]: AGREEMENT_FIELD_GROUPS.AUTO,
  [AGREEMENT_FIELD_TYPES.PHONE]: AGREEMENT_FIELD_GROUPS.BUYER,
  [AGREEMENT_FIELD_TYPES.COMPANY]: AGREEMENT_FIELD_GROUPS.BUYER,
  [AGREEMENT_FIELD_TYPES.ABN]: AGREEMENT_FIELD_GROUPS.BUYER,
  [AGREEMENT_FIELD_TYPES.FUNDING_SOURCE]: AGREEMENT_FIELD_GROUPS.BUYER,
  [AGREEMENT_FIELD_TYPES.PURCHASE_BUDGET]: AGREEMENT_FIELD_GROUPS.BUYER,
  [AGREEMENT_FIELD_TYPES.CUSTOM_TEXT]: AGREEMENT_FIELD_GROUPS.BUYER,
  [AGREEMENT_FIELD_TYPES.CHECKBOX]: AGREEMENT_FIELD_GROUPS.BUYER,
};

export const AGREEMENT_EXECUTION_STATUSES = {
  PENDING: 'pending',
  EXECUTED: 'executed',
} as const;

export type AgreementExecutionStatus = ObjectValues<
  typeof AGREEMENT_EXECUTION_STATUSES
>;

export const SIGNATURE_METHODS = {
  DRAWN: 'drawn',
  TYPED: 'typed',
} as const;

export type SignatureMethod = ObjectValues<typeof SIGNATURE_METHODS>;

export const AGREEMENT_EXECUTION_ROLES = {
  BUYER: 'buyer',
  BROKER: 'broker',
} as const;

export type AgreementExecutionRole = ObjectValues<
  typeof AGREEMENT_EXECUTION_ROLES
>;

export const AGREEMENT_ERROR_CODES = {
  FILE_INVALID: 'AGREEMENT_FILE_INVALID',
  FILE_TOO_LARGE: 'AGREEMENT_FILE_TOO_LARGE',
  FILE_ENCRYPTED: 'AGREEMENT_FILE_ENCRYPTED',
  FILE_PAGE_COUNT_INVALID: 'AGREEMENT_FILE_PAGE_COUNT_INVALID',
  FIELDS_INVALID: 'AGREEMENT_FIELDS_INVALID',
  DOCUSIGN_CONNECTED: 'AGREEMENT_DOCUSIGN_CONNECTED',
  LAST_NAME_REQUIRED: 'AGREEMENT_LAST_NAME_REQUIRED',
  TOKEN_INVALID: 'AGREEMENT_TOKEN_INVALID',
  CODE_RATE_LIMITED: 'AGREEMENT_CODE_RATE_LIMITED',
  CODE_INVALID: 'AGREEMENT_CODE_INVALID',
  CODE_EXPIRED: 'AGREEMENT_CODE_EXPIRED',
  CODE_ATTEMPTS_EXCEEDED: 'AGREEMENT_CODE_ATTEMPTS_EXCEEDED',
  EMAIL_NOT_VERIFIED: 'AGREEMENT_EMAIL_NOT_VERIFIED',
  ALREADY_EXECUTED: 'AGREEMENT_ALREADY_EXECUTED',
  PROCESSING: 'AGREEMENT_PROCESSING',
} as const;

export const AGREEMENT_FIELDS_INVALID_REASONS = {
  SIGNATURE_REQUIRED: 'signature_required',
  LABEL_INVALID: 'label_invalid',
} as const;

export const AGREEMENT_ERROR_STATUSES = {
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE: 422,
  TOO_MANY_REQUESTS: 429,
} as const;

export const AGREEMENT_LIMITS = {
  FIELD_VALUE_MAX_LENGTH: 200,
  FIELD_LABEL_MAX_LENGTH: 100,
  MAX_FIELDS: 50,
  MIN_FIELD_SIZE: 10,
  MAX_FILE_SIZE_MB: 15,
  MAX_PAGES: 100,
  SIGNATURE_IMAGE_MAX_BYTES: 500 * 1024,
} as const;

export type AgreementField = {
  id: string;
  type: AgreementFieldType;
  label?: string;
  required: boolean;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  maxLength?: number;
};

export type AgreementPage = {
  page: number;
  width: number;
  height: number;
  rotation: 0 | 90 | 180 | 270;
};

export type AgreementVersion = {
  _id: string;
  versionNo: number;
  originalFilename: string;
  pageCount: number;
  pages: AgreementPage[];
  sha256: string;
  deedWordingFlagged: boolean;
  deedWordingMatches: string[];
  fields: AgreementField[];
  locked: boolean;
  pdfUrl: string;
  createdAt: string;
};

export type AgreementTemplate = {
  _id: string;
  listingId: string;
  messageToBuyers?: string;
  isActive: boolean;
  currentVersion: AgreementVersion | null;
};

export type AgreementExecutionSummary = {
  _id: string;
  listingId: string;
  listingTitle: string;
  leadId: string;
  status: AgreementExecutionStatus;
  versionNo: number;
  buyer: {
    name: string;
    entity?: string;
    abn?: string;
    email: string;
    mobile?: string;
  };
  broker: { name: string; entity?: string; email: string };
  signedAt?: string;
  executedSha256?: string;
  createdAt: string;
};

export type AgreementSigningSession = {
  executionId: string;
  status: AgreementExecutionStatus;
  dealName: string;
  listingTitle: string;
  brokerName: string;
  messageToBuyers?: string;
  version: Pick<
    AgreementVersion,
    '_id' | 'versionNo' | 'pageCount' | 'pages' | 'fields' | 'pdfUrl'
  >;
  prefill: Record<string, string>;
  emailMasked: string;
  emailVerified: boolean;
  emailVerificationMethod?: AgreementEmailVerificationMethod;
  consentText: string;
  signedAt?: string;
  executedSha256?: string;
  downloadAvailable: boolean;
  context?: AgreementSigningContext;
};

export type AgreementPaginated<T> = {
  items: T[];
  total: number;
  page: number;
  limit: number;
};

export type AgreementPaginationParams = {
  page?: number;
  limit?: number;
};

export type ActivateAgreementBody = {
  file: File;
  fields: AgreementField[];
  messageToBuyers?: string;
};

export type SignAgreementBody = {
  versionId: string;
  fieldValues: Record<string, string>;
  signatureImage: string;
  signatureMethod: SignatureMethod;
  consentAccepted: true;
  timezone?: string;
};

export const AGREEMENT_SIGNING_CONTEXT_TYPES = {
  LEAD: 'lead',
  SECURE_LINK: 'secure_link',
} as const;

export type AgreementSigningContext =
  | { type: typeof AGREEMENT_SIGNING_CONTEXT_TYPES.LEAD }
  | {
      type: typeof AGREEMENT_SIGNING_CONTEXT_TYPES.SECURE_LINK;
      shareableLinkId: string;
      shareableLinkName: string;
    };

export const AGREEMENT_EMAIL_VERIFICATION_METHODS = {
  CODE: 'code',
  SESSION: 'session',
} as const;

export type AgreementEmailVerificationMethod = ObjectValues<
  typeof AGREEMENT_EMAIL_VERIFICATION_METHODS
>;
